import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Container, Card, Spinner, Alert, Button } from 'react-bootstrap';
import { getSurveyShareByToken } from 'services/surveyShares';
import PublicDashboard from 'components/dashboards/public/PublicDashboard';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSyncAlt, faPlay, faPause } from '@fortawesome/free-solid-svg-icons';

const SharedReport = () => {
  const { token } = useParams();
  const [shareData, setShareData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [intervalId, setIntervalId] = useState(null);

  useEffect(() => {
    if (token) {
      fetchSharedReport();
    } else {
      setError('Token no válido');
      setLoading(false);
    }
  }, [token, refreshKey]);

  const fetchSharedReport = async () => {
    try {
      const response = await getSurveyShareByToken(token);
      console.log(response);

      // La respuesta viene directamente con los datos del share
      if (response && response.id && response.token) {
        setShareData(response);
        setError(null);
        setLastUpdated(new Date());
      } else {
        setError('URL no válida o reporte no encontrado');
      }
    } catch (err) {
      console.error('Error al obtener el reporte compartido:', err);
      setError('URL no válida o reporte no encontrado');
    }
    setLoading(false);
    setIsRefreshing(false);
  };

  const handleRefresh = () => {
    setIsRefreshing(true);
    setRefreshKey(prev => prev + 1);
  };

  const toggleAutoRefresh = () => {
    if (autoRefresh) {
      // Detener auto-refresh
      if (intervalId) {
        clearInterval(intervalId);
        setIntervalId(null);
      }
      setAutoRefresh(false);
    } else {
      // Iniciar auto-refresh cada 5 minutos
      const newIntervalId = setInterval(() => {
        handleRefresh();
      }, 5 * 60 * 1000); // 5 minutos
      setIntervalId(newIntervalId);
      setAutoRefresh(true);
    }
  };

  // Limpiar interval al desmontar componente
  useEffect(() => {
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [intervalId]);

  const formatDateTime = date => {
    if (!date) return '';
    return date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  if (loading) {
    return (
      <Container fluid className="py-5">
        <div className="text-center">
          <Spinner animation="border" role="status" className="me-2" />
          <span>Cargando reporte compartido...</span>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container fluid className="py-5">
        <Card>
          <Card.Body>
            <Alert variant="danger">
              <Alert.Heading>Error</Alert.Heading>
              <p>{error}</p>
            </Alert>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  if (!shareData || !shareData.is_valid) {
    return (
      <Container fluid className="py-5">
        <Card>
          <Card.Body>
            <Alert variant="danger">
              <Alert.Heading>URL no válida</Alert.Heading>
              <p>
                Esta URL de reporte compartido no es válida o ha sido
                desactivada.
              </p>
            </Alert>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  return (
    <Container fluid>
      {/* Título principal discreto pero prominente */}
      <div className="text-center mb-2 py-3">
        <h2 className="fw-bold text-dark mb-3">
          {shareData.survey?.name || 'Reporte de Encuesta'}
        </h2>
        <p className="text-muted small mb-0">
          Reporte compartido para:{' '}
          <span className="fw-bold text-primary">{shareData.person_name}</span>
        </p>
      </div>

      <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-3 gap-3">
        <div className="text-muted small">
          {lastUpdated && (
            <>
              <strong>Última actualización:</strong>{' '}
              {formatDateTime(lastUpdated)}
              {isRefreshing && (
                <span className="ms-2 text-primary">
                  <FontAwesomeIcon icon={faSyncAlt} className="fa-spin me-1" />
                  Actualizando...
                </span>
              )}
            </>
          )}
        </div>
        <div className="d-flex flex-column flex-sm-row gap-2 w-100 w-md-auto">
          <Button
            variant={autoRefresh ? 'success' : 'outline-secondary'}
            size="sm"
            onClick={toggleAutoRefresh}
            className="d-flex align-items-center justify-content-center"
            title={
              autoRefresh
                ? 'Desactivar recarga automática (cada 5 min)'
                : 'Activar recarga automática cada 5 minutos'
            }
          >
            <FontAwesomeIcon
              icon={autoRefresh ? faPause : faPlay}
              className="me-2"
            />
            {autoRefresh ? 'Recarga Automática' : 'Recarga Manual'}
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleRefresh}
            disabled={loading || isRefreshing}
            className="d-flex align-items-center justify-content-center"
          >
            <FontAwesomeIcon
              icon={faSyncAlt}
              className={`me-2 ${loading || isRefreshing ? 'fa-spin' : ''}`}
            />
            {isRefreshing ? 'Recargando...' : 'Recargar Ahora'}
          </Button>
        </div>
      </div>

      {/* Renderizar el dashboard público con el survey específico */}
      <PublicDashboard surveyId={shareData.survey_id} refreshKey={refreshKey} />
    </Container>
  );
};

export default SharedReport;
